import React, { useState, useContext, useEffect } from "react";
import { AppSettings } from "../../config/app-settings.js";
import useAuthStore from "../../stores/auth-store.js";
import useEvacuationStore from "../../stores/evacuation-store.js";
import MapComponent from "../../components/map/evacuation-map.jsx";
import EvacuationPortalSelector from "../../components/selector/evacuation-portal-selector.jsx";

function GroundTruthBuildings() {
  const context = useContext(AppSettings);
  const user = useAuthStore((state) => state.user);

  // Get layer data from Zustand store
  const visibleLayers = useEvacuationStore((state) => state.visibleLayers);
  const staticLayers = useEvacuationStore((state) => state.staticLayers);

  // Setup app layout
  useEffect(() => {
    context.handleSetAppContentClass('');
    context.handleSetAppSidebarNone(true);
    context.handleSetAppSidebarTwo(false);
    context.handleSetAppSidebarWide(false);
    context.handleSetAppTopMenu(true);

    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppTopMenu(false);
    };
  }, [context]);

  // Form state
  const [formData, setFormData] = useState({
    zoneId: "15",
    address: "",
    occupied: "no",
    departureTime: "",
    familyMembers: "",
    vehiclesToDepart: "",
    destination: "",
    destinationOther: "",
    notes: "",
  });

  const [selectedLocation, setSelectedLocation] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Ground Truth Building Data Submitted:", formData);
    console.log("Selected Location:", selectedLocation);
    alert("Ground Truth Building data submitted (cosmetic only)");
  };

  const handleReset = () => {
    setFormData({
      zoneId: "15",
      address: "",
      occupied: "no",
      departureTime: "",
      familyMembers: "",
      vehiclesToDepart: "",
      destination: "",
      destinationOther: "",
      notes: "",
    });
    setSelectedLocation(null);
  };

  const handleMapClick = (latLng) => {
    setSelectedLocation(latLng);
    // In a real implementation, this would geocode to an address
    setFormData((prev) => ({
      ...prev,
      address: `Selected: ${latLng.lat.toFixed(4)}, ${latLng.lng.toFixed(4)}`,
    }));
  };

  return (
    <>
      {/* Map and Form Layout */}
      <div className="container-fluid p-2" style={{ minHeight: `calc(100vh - var(--app-header-height, 60px) - var(--app-top-menu-height, 40px) - 30px)` }}>
        <div className="row g-2" style={{ minHeight: "100%" }}>
        {/* Map Section - Column 9 (Full Height on Desktop, Normal on Mobile) */}
        <div className="col-lg-9 d-flex flex-column" style={{ minHeight: "600px" }}>
          <div className="card border-0 shadow-lg d-flex flex-column h-100">
            <div className="card-header bg-dark text-white flex-shrink-0">
              <h5 className="mb-0">
                <i className="fa fa-map me-2"></i>
                Select Address on Map
              </h5>
            </div>
            <div className="card-body p-0 flex-grow-1 d-flex flex-column">
              <div style={{ flex: "1", width: "100%" }}>
                <MapComponent
                  center={[49.8844, -119.4859]}
                  zoom={12}
                  height="100%"
                  width="100%"
                  clickable={true}
                  onMapClick={handleMapClick}
                />
              </div>
              {selectedLocation && (
                <div className="p-2 bg-light border-top flex-shrink-0">
                  <small className="text-muted">
                    <i className="fa fa-map-pin me-2"></i>
                    Selected: {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Section - Column 3 (Centered on Desktop, Full Width Stack on Mobile) */}
        <div className="col-lg-3 d-flex flex-column" style={{ justifyContent: "center", alignItems: "stretch" }}>
          <div className="card border-0 shadow-lg" style={{ display: "flex", flexDirection: "column", maxHeight: "calc(100vh - var(--app-header-height, 60px) - var(--app-top-menu-height, 40px) - 60px)" }}>
            <div className="card-header bg-dark text-white flex-shrink-0">
              <h5 className="mb-0">
                <i className="fa fa-building me-2"></i>
                Ground Truth - Field Crew (Buildings)
              </h5>
            </div>

            <div className="card-body p-3 overflow-y-auto" style={{ flex: "1 1 auto", minHeight: 0 }}>
              <form id="buildingsForm" onSubmit={handleSubmit}>
                {/* Zone and Address Section */}
                <div className="mb-3">
                  {/* Row 1: Zone ID */}
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <label htmlFor="zoneId" className="form-label small fw-bold">
                        Zone ID
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="zoneId"
                        name="zoneId"
                        value={formData.zoneId}
                        onChange={handleInputChange}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>

                  {/* Row 2: Home Address (Full Width) */}
                  <div className="row">
                    <div className="col-12 mb-2">
                      <label htmlFor="address" className="form-label small fw-bold">
                        Home Address (click map to select)
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Click on map to select"
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                {/* Occupancy Section */}
                <div className="mb-3">
                  {/* Row 1: Occupied */}
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <label htmlFor="occupied" className="form-label small fw-bold">
                        Occupied?
                      </label>
                      <select
                        className="form-select form-select-sm"
                        id="occupied"
                        name="occupied"
                        value={formData.occupied}
                        onChange={handleInputChange}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Departure & Family Members */}
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <label htmlFor="departureTime" className="form-label small fw-bold">
                        Departure (min)
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        id="departureTime"
                        name="departureTime"
                        value={formData.departureTime}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        disabled={formData.occupied === "no"}
                      />
                    </div>

                    <div className="col-md-6 mb-2">
                      <label htmlFor="familyMembers" className="form-label small fw-bold">
                        Family #
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        id="familyMembers"
                        name="familyMembers"
                        value={formData.familyMembers}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        disabled={formData.occupied === "no"}
                      />
                    </div>
                  </div>

                  {/* Row 3: Vehicles & Destination */}
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <label htmlFor="vehiclesToDepart" className="form-label small fw-bold">
                        Vehicles
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        id="vehiclesToDepart"
                        name="vehiclesToDepart"
                        value={formData.vehiclesToDepart}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        disabled={formData.occupied === "no"}
                      />
                    </div>

                    <div className="col-md-6 mb-2">
                      <label htmlFor="destination" className="form-label small fw-bold">
                        Destination
                      </label>
                      <select
                        className="form-select form-select-sm"
                        id="destination"
                        name="destination"
                        value={formData.destination}
                        onChange={handleInputChange}
                        disabled={formData.occupied === "no"}
                      >
                        <option value="">Select...</option>
                        <option value="muster">Muster Station</option>
                        <option value="hotel">Hotel</option>
                        <option value="family">Family/Friend</option>
                        <option value="campground">Campground</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Row 3: Destination Other (shown when occupied=yes and destination=other) */}
                  {formData.occupied === "yes" && formData.destination === "other" && (
                    <div className="row">
                      <div className="col-12 mb-2">
                        <label htmlFor="destinationOther" className="form-label small fw-bold">
                          Specify Destination
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          id="destinationOther"
                          name="destinationOther"
                          value={formData.destinationOther}
                          onChange={handleInputChange}
                          placeholder="Specify other destination..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes and Photo Section */}
                <div className="row">
                  <div className="col-12 mb-2">
                    <label htmlFor="notes" className="form-label small fw-bold">
                      Notes
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      id="notes"
                      name="notes"
                      rows="2"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Additional observations..."
                    ></textarea>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 mb-2">
                    <button type="button" className="btn btn-secondary btn-sm w-100">
                      <i className="fa fa-camera me-1"></i>
                      Take Photo
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="card-footer bg-light border-top p-2 d-flex gap-2 justify-content-end flex-shrink-0">
              <button
                type="reset"
                form="buildingsForm"
                className="btn btn-secondary btn-sm"
              >
                <i className="fa fa-redo me-2"></i>
                Reset
              </button>
              <button type="submit" form="buildingsForm" className="btn btn-primary btn-sm">
                <i className="fa fa-save me-2"></i>
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Layer Controls - After Map and Form */}
      <EvacuationPortalSelector pageContext="ground-truth-buildings" />
    </>
  );
}

export default GroundTruthBuildings;
